import axios from 'axios';
import React, { useEffect, useState } from 'react';
import {
  FaArrowLeft,
  FaBox,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaReceipt,
  FaTimesCircle,
  FaTruck
} from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated && id) {
      fetchOrder();
    }
  }, [isAuthenticated, id]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load order details');
      if (error.response?.status === 404 || error.response?.status === 403) {
        navigate('/orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: FaClock, text: 'Pending' },
      confirmed: { color: 'bg-blue-100 text-blue-800', icon: FaCheckCircle, text: 'Confirmed' },
      processing: { color: 'bg-purple-100 text-purple-800', icon: FaClock, text: 'Processing' },
      shipped: { color: 'bg-indigo-100 text-indigo-800', icon: FaTruck, text: 'Shipped' },
      delivered: { color: 'bg-green-100 text-green-800', icon: FaCheckCircle, text: 'Delivered' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: FaTimesCircle, text: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${config.color}`}>
        <Icon /> {config.text}
      </span>
    );
  };

  const getShippingStatusBadge = (shippingStatus) => {
    const statusConfig = {
      not_shipped: { color: 'bg-gray-100 text-gray-800', text: 'Not Shipped', icon: FaBox },
      preparing: { color: 'bg-yellow-100 text-yellow-800', text: 'Preparing', icon: FaClock },
      shipped: { color: 'bg-blue-100 text-blue-800', text: 'Shipped', icon: FaTruck },
      out_for_delivery: { color: 'bg-indigo-100 text-indigo-800', text: 'Out for Delivery', icon: FaTruck },
      delivered: { color: 'bg-green-100 text-green-800', text: 'Delivered', icon: FaCheckCircle }
    };

    const config = statusConfig[shippingStatus] || statusConfig.not_shipped;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${config.color}`}>
        <Icon /> {config.text}
      </span>
    );
  };

  const formatCurrency = (amount, currency = 'INR') => {
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₹';
    return `${symbol}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownloadInvoice = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/invoices/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${order.orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to download invoice');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">Loading order details...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Order not found</p>
          <Link to="/orders" className="text-primary hover:underline">
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/orders"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-primary mb-4 transition"
          >
            <FaArrowLeft /> Back to Orders
          </Link>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Order #{order.orderNumber}
              </h1>
              <p className="text-gray-500">Placed on {formatDate(order.createdAt)}</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center gap-3">
              {getStatusBadge(order.status)}
              <button
                onClick={handleDownloadInvoice}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
              >
                <FaReceipt /> Download Invoice
              </button>
            </div>
          </div>
        </div>

        {/* Delivery Status */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Delivery Status</h2>
          <div className="flex items-center gap-4 mb-4">
            {getShippingStatusBadge(order.shippingStatus || 'not_shipped')}
            {order.trackingNumber && (
              <div>
                <p className="text-sm text-gray-500">Tracking Number</p>
                <p className="text-lg font-semibold">{order.trackingNumber}</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order Items */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Order Items</h2>
              <div className="space-y-4">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex gap-4 pb-4 border-b last:border-0">
                    {item.product?.images?.[0] && (
                      <img
                        src={item.product.images[0]}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold">{item.name}</h3>
                      <p className="text-sm text-gray-500">
                        Quantity: {item.quantity} × {formatCurrency(item.discountedPrice || item.price, item.currency)}
                      </p>
                      <p className="font-semibold mt-1">
                        {formatCurrency((item.discountedPrice || item.price) * item.quantity, item.currency)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary & Details */}
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{formatCurrency(order.subtotal, order.currency)}</span>
                </div>
                {order.shipping > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span>{formatCurrency(order.shipping, order.currency)}</span>
                  </div>
                )}
                {order.tax > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax</span>
                    <span>{formatCurrency(order.tax, order.currency)}</span>
                  </div>
                )}
                {order.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(order.discount, order.currency)}</span>
                  </div>
                )}
              </div>
              <div className="border-t pt-2 flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-primary">{formatCurrency(order.total, order.currency)}</span>
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <FaCreditCard /> Payment Information
              </h2>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Method</span>
                  <span className="font-medium capitalize">
                    {order.payment?.method || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status</span>
                  <span
                    className={`font-medium ${
                      order.payment?.status === 'completed'
                        ? 'text-green-600'
                        : order.payment?.status === 'failed'
                        ? 'text-red-600'
                        : 'text-yellow-600'
                    }`}
                  >
                    {order.payment?.status || 'Pending'}
                  </span>
                </div>
                {order.payment?.paidAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paid At</span>
                    <span className="text-sm">{formatDate(order.payment.paidAt)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <FaMapMarkerAlt /> Shipping Address
              </h2>
              <div className="text-gray-700">
                <p className="font-medium">{order.customer.name}</p>
                <p className="mt-1">{order.customer.address.street}</p>
                <p>
                  {order.customer.address.city}, {order.customer.address.state}{' '}
                  {order.customer.address.zipCode}
                </p>
                <p>{order.customer.address.country}</p>
                <div className="mt-3 space-y-1">
                  <p className="flex items-center gap-2 text-sm">
                    <FaPhone /> {order.customer.phone}
                  </p>
                  <p className="flex items-center gap-2 text-sm">
                    <FaEnvelope /> {order.customer.email}
                  </p>
                </div>
              </div>
            </div>

            {order.notes && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FaReceipt /> Order Notes
                </h2>
                <p className="text-gray-700">{order.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;

